import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { trackFAQExpanded } from '@/lib/analytics';

interface FAQ {
  question: string;
  answer: string;
}

const FAQS: FAQ[] = [
  {
    question: 'Can I change my plan later?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing so you only pay for what you use.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe. All payments are encrypted and secure.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Absolutely! You can cancel your subscription at any time from your account settings. Your subscription will remain active until the end of your current billing period, and you\'ll continue to have access to all features until then.',
  },
  {
    question: 'What happens if I cancel?',
    answer: 'When you cancel, you\'ll keep access to all premium features until the end of your current billing period. After that, your account will revert to the free tier. All your data and travel history will be preserved.',
  },
  {
    question: 'How does billing work?',
    answer: 'Subscriptions are billed monthly or annually on the same date you signed up. We\'ll send you an email receipt for each payment. You can view your billing history in your account settings.',
  },
  {
    question: 'Is my payment information secure?',
    answer: 'Yes! We use Stripe, a PCI-compliant payment processor trusted by millions of businesses. We never store your full credit card information on our servers. All payment data is encrypted and handled securely.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'We don\'t currently offer a free trial, but we do have a free tier that lets you explore our basic features. You can upgrade anytime to unlock premium features and start saving on every trip.',
  },
];

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="max-w-3xl mx-auto px-4 mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 text-white drop-shadow-lg">
        Everything you need to know
      </h2>
      
      <div className="space-y-3 md:space-y-4">
        {FAQS.map((faq, idx) => (
          <Collapsible
            key={idx}
            open={openIndex === idx}
            onOpenChange={(open) => {
              setOpenIndex(open ? idx : null);
              if (open) {
                trackFAQExpanded(faq.question);
              }
            }}
          >
            <div className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl overflow-hidden">
              <CollapsibleTrigger className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-white/20 dark:hover:bg-zinc-700/50 transition-colors min-h-[44px]">
                <span className="font-semibold text-white pr-4 text-sm md:text-base">{faq.question}</span>
                <ChevronDown
                  className={cn(
                    'w-5 h-5 md:w-6 md:h-6 text-[#5B6FED] flex-shrink-0 transition-transform duration-200',
                    openIndex === idx && 'rotate-180'
                  )}
                />
              </CollapsibleTrigger>
              
              <CollapsibleContent className="px-5 md:px-6 pb-5 md:pb-6">
                <div className="pt-2 text-white/80 leading-relaxed text-sm md:text-base">
                  {faq.answer}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        ))}
      </div>
    </section>
  );
}

